import { enqueueSnackbar } from 'notistack';
import { ApiService, RetrieveDocumentGenerationJob } from '../api';
import { getGenerationJobFailureMessage } from './generationJobs';
// Direct .type import keeps this service free of the context barrel's React modules.
import { EventType } from '../context/EventBusContext/EventBusContext.type';
import { Logger } from '../utilities/logger';

// This lives apart from generationJobs.ts on purpose: that module is a pure leaf
// (constants + parsing) imported by lightweight UI hooks, while this one drags in
// the API client. Keep the effectful completion handling here.

/**
 * Build the terminal-state (onResult) handler shared by every cap-table
 * generation producer: the Process drawer and the app-load resume path (jobs
 * picked up again after a reload). Both poll the same job endpoint and apply
 * results through the same endpoint, so completion behavior must be identical -
 * apply the results, announce the applied security count, or warn honestly when
 * the engine extracted nothing. We cannot infer a no-data document was "wrong"
 * (the user referenced it or the classifier tagged it as a cap table), so state
 * only the observed fact rather than a green success. When the server reports
 * already_applied (another caller won the exactly-once guard), refresh the grid
 * and clear the job but announce nothing - whoever applied it announced it.
 */
export const createCapTableGenerationOnResult = ({
  companyId,
  logContext,
  publish,
  removeJob,
  onApplyError,
}: {
  companyId: string;
  /** Producer name prefixed to error logs so each registration site stays identifiable. */
  logContext: string;
  publish: (eventType: EventType, payload: unknown) => void;
  removeJob: (jobId: string) => void;
  /**
   * Invoked when applying a COMPLETED job's results fails. The job is still
   * cleared from the tracker (its polling is done), but the backend never
   * stamped applied_at, so callers that keep a session-level
   * once-guard must unlatch it here to keep the apply retryable. A callback
   * rather than a return value / rethrow because AsyncProcessorContext types
   * onResult as Promise<void> and invokes it without catching.
   */
  onApplyError?: (result: RetrieveDocumentGenerationJob, error: unknown) => void;
}) =>
  async (result: RetrieveDocumentGenerationJob): Promise<void> => {
    if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
      try {
        const applyResult = (await ApiService.apiCompanyCapTablesApplyGenerationJobCreate(companyId, {
          job_id: result.job_id,
        })) as unknown as { already_applied?: boolean; security_ids?: number[] };

        // Publish even when already applied elsewhere - this tab's grid may be stale.
        publish(EventType.CAP_TABLE_GENERATION_JOB_COMPLETED, result.job_id);
        const count = applyResult?.security_ids?.length ?? 0;
        if (!applyResult?.already_applied) {
          enqueueSnackbar(
            count > 0
              ? `Cap table updated with ${count} securities from AI results`
              : 'Processing completed, but no cap table data could be extracted from the selected document(s). ' +
                  'Double-check that the documents contain cap table information.',
            { variant: count > 0 ? 'success' : 'warning' }
          );
        }
      } catch (applyError) {
        Logger.error(`${logContext} - Error applying cap table generation job`, applyError);
        enqueueSnackbar('Job completed but applying results to the cap table failed', {
          variant: 'warning',
        });
        onApplyError?.(result, applyError);
      }
      removeJob(result.job_id);
    } else if (result.status === RetrieveDocumentGenerationJob.status.FAILED) {
      enqueueSnackbar(getGenerationJobFailureMessage(result, 'Cap table processing job failed'), {
        variant: 'error',
      });
      removeJob(result.job_id);
    }
  };

export default createCapTableGenerationOnResult;
